import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import type { PoiSource } from "@poi-planner/shared";
import { Button } from "./Button";
import { FormInput } from "./FormFields";
import { externalHref, linkHost } from "../utils/externalLinks";

function isBlank(source: PoiSource): boolean {
  return !source.text.trim() && !source.url?.trim();
}

function SourceEditRow({
  source,
  index,
  suggestionsId,
  onChange,
  onFocus,
  onDone,
  onRemove,
}: {
  source: PoiSource;
  index: number;
  suggestionsId: string;
  onChange: (source: PoiSource) => void;
  /** Marks the row as the one being edited, so typing into a blank row
   * doesn't flip it to the read view after the first character. */
  onFocus: () => void;
  onDone: () => void;
  onRemove: () => void;
}) {
  const containerRef = useRef<HTMLLIElement>(null);
  const textId = `${suggestionsId}-text-${index}`;
  const urlId = `${suggestionsId}-url-${index}`;

  // New rows open ready to type.
  useEffect(() => {
    containerRef.current?.querySelector("input")?.focus();
  }, []);

  const commitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onDone();
    }
  };

  return (
    <li
      ref={containerRef}
      onFocus={onFocus}
      className="flex flex-col gap-2 rounded-lg bg-subtle p-3"
    >
      <div>
        <label htmlFor={textId} className="form-label">
          Where did you find it?
        </label>
        <FormInput
          id={textId}
          value={source.text}
          onChange={(text) => onChange({ ...source, text })}
          onKeyDown={commitOnEnter}
          list={suggestionsId}
          placeholder="e.g. Mark's Tokyo Tips, Eater NY"
          className="bg-surface"
        />
      </div>
      <div>
        <label htmlFor={urlId} className="form-label">
          Link <span className="font-normal text-tertiary">(optional)</span>
        </label>
        <FormInput
          id={urlId}
          type="url"
          inputMode="url"
          value={source.url ?? ""}
          onChange={(url) => onChange({ ...source, url })}
          onKeyDown={commitOnEnter}
          placeholder="https://"
          className="bg-surface"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onRemove}>
          Remove
        </Button>
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </li>
  );
}

function SourceViewRow({
  source,
  onEdit,
  onRemove,
  disabled,
}: {
  source: PoiSource;
  onEdit: () => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const href = source.url ? externalHref(source.url) : null;
  const label = source.text.trim() || (href ? linkHost(href) : "Source");
  return (
    <li className="flex items-center gap-2.5 rounded-lg border border-border py-2 pl-3 pr-1.5">
      <span
        className="material-symbols-outlined text-lg text-tertiary"
        aria-hidden
      >
        {href ? "link" : "bookmark"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="break-words text-sm font-semibold text-primary">
          {label}
        </div>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-0.5 text-xs font-semibold text-info no-underline hover:underline"
          >
            <span className="truncate">{linkHost(href)}</span>
            <span
              className="material-symbols-outlined icon-match-text"
              aria-hidden
            >
              arrow_outward
            </span>
          </a>
        ) : source.url?.trim() ? (
          <span className="block truncate text-xs text-tertiary">
            {source.url}
          </span>
        ) : (
          <span className="text-xs text-tertiary">No link</span>
        )}
      </div>
      {!disabled && (
        <>
          <Button
            variant="icon"
            size="sm"
            icon="edit"
            aria-label={`Edit ${label}`}
            onClick={onEdit}
          />
          <Button
            variant="icon"
            size="sm"
            icon="close"
            aria-label={`Remove ${label}`}
            onClick={onRemove}
          />
        </>
      )}
    </li>
  );
}

/**
 * Where a POI recommendation came from. Saved sources read as a list with
 * clickable links; editing one (or adding a new, blank one) swaps that row
 * for its inputs.
 */
export function PoiSourcesEditor({
  sources,
  allSources,
  onChange,
  disabled = false,
}: {
  sources: PoiSource[];
  /** Earlier source names across the library, offered while typing. */
  allSources: string[];
  onChange: (sources: PoiSource[]) => void;
  disabled?: boolean;
}) {
  const suggestionsId = useId();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const finishEditing = (index: number) => {
    setEditingIndex(null);
    if (isBlank(sources[index])) {
      onChange(sources.filter((_, i) => i !== index));
    }
  };

  const remove = (index: number) => {
    setEditingIndex(null);
    onChange(sources.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      {sources.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {sources.map((source, index) =>
            !disabled && (index === editingIndex || isBlank(source)) ? (
              <SourceEditRow
                key={index}
                source={source}
                index={index}
                suggestionsId={suggestionsId}
                onChange={(next) =>
                  onChange(sources.map((s, i) => (i === index ? next : s)))
                }
                onFocus={() => setEditingIndex(index)}
                onDone={() => finishEditing(index)}
                onRemove={() => remove(index)}
              />
            ) : (
              <SourceViewRow
                key={index}
                source={source}
                disabled={disabled}
                onEdit={() => setEditingIndex(index)}
                onRemove={() => remove(index)}
              />
            ),
          )}
        </ul>
      )}
      <datalist id={suggestionsId}>
        {allSources.map((text) => (
          <option key={text} value={text} />
        ))}
      </datalist>
      {!disabled && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            icon="add"
            onClick={() => {
              setEditingIndex(null);
              onChange([...sources, { text: "", url: "" }]);
            }}
            className="-ml-2"
          >
            Add source
          </Button>
        </div>
      )}
    </div>
  );
}
